import React, { useEffect, useMemo, useState } from "react";

import { useForm, Control, FieldErrors } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import { HiInformationCircle, HiOutlineCube } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import useAuth from "../../../../hooks/useAuth";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import {
  EmailSenderValidationStatus,
  useEmailSenderValidationStatus,
  useRequestEmailSenderValidation,
  useUpdateEntityPreferencesByPath,
} from "../../../../hooks/entity";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { isReservedSenderDomain } from "../../../../utils/emailSender";
import UpgradeModal from "../../../modals/UpgradeModal";
import EmailSenderValidationModal from "../../../modals/EmailSenderValidationModal";
import { PremiumBenefit } from "../../../atoms/UpgradePromptContent";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import EmailDomainCard from "./EmailDomainCard";
import { FieldLabel } from "../../../atoms/FieldLabel";

export type EmailSenderSettingsTypes = {
  data: any;
  user_identification: string;
  entity_identification: string;
};

type FormValues = {
  email_address_sender: string;
};

export const senderEmailSchema = Joi.string()
  .trim()
  .email({ tlds: { allow: false } })
  .required()
  .label("Sender email address");

const schema = Joi.object({
  email_address_sender: senderEmailSchema,
}).required();

type SenderEmailFieldProps = {
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  stored: string;
  current: string;
  emailValidationStatus: EmailSenderValidationStatus | undefined;
  isRequestingCode: boolean;
  isSaving: boolean;
  canSave: boolean;
  canWriteEntitySettings: boolean;
  onRequestCode: () => void;
};

const SenderEmailField: React.FC<SenderEmailFieldProps> = ({
  control,
  errors,
  stored,
  current,
  emailValidationStatus,
  isRequestingCode,
  isSaving,
  canSave,
  canWriteEntitySettings,
  onRequestCode,
}) => {
  const { t } = useTranslation();

  const storedLc = stored.toLowerCase();
  const currentLc = current.toLowerCase();
  const isDirty = storedLc !== currentLc;

  const validatedEmailLc = emailValidationStatus?.email?.toLowerCase() ?? "";
  const isValidated =
    !!emailValidationStatus?.is_valid &&
    storedLc.length > 0 &&
    validatedEmailLc === storedLc &&
    !isDirty;

  const isDefaultDomain = currentLc ? isReservedSenderDomain(currentLc) : false;

  const showUpdateButton = isDirty && !isDefaultDomain;
  const canVerify = !!stored && !isDirty && !isDefaultDomain;
  const showVerifyButton = !showUpdateButton && canVerify && !isValidated;

  return (
    <div className="mb-3 w-full">
      <FieldLabel
        htmlFor="email_address_sender"
        className="text-sm font-medium text-gray-700 block mb-1"
      >
        {t("dashboard.entities.entityPreferences.label-03")}
      </FieldLabel>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <CustomInput
            name="email_address_sender"
            control={control}
            error={errors.email_address_sender}
            helperText={errors.email_address_sender?.message}
            grayBackground={true}
            type="email"
            maxLength={150}
          />
        </div>
        {showUpdateButton && canWriteEntitySettings && (
          <CustomButton
            type="submit"
            variant="primary"
            disabled={!canSave || isSaving}
            isLoading={isSaving}
            title={t("common.update")}
          />
        )}
        {showVerifyButton && canWriteEntitySettings && (
          <CustomButton
            type="button"
            variant="primary"
            disabled={isRequestingCode}
            isLoading={isRequestingCode}
            onClick={onRequestCode}
            title={t("dashboard.entities.emailSenderValidation.btnVerifyNow")}
          />
        )}
      </div>
      {isDefaultDomain && (
        <p className="mt-1 flex items-start gap-1 text-sm text-amber-700 font-figtree">
          <HiInformationCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {t("dashboard.entities.emailSenderValidation.defaultDomainMessage")}
          </span>
        </p>
      )}
      <CharacterCounter currentLength={current.length} maxLength={150} />
    </div>
  );
};

const EmailSenderSettings = ({
  entity_identification,
  user_identification,
  data,
}: EmailSenderSettingsTypes) => {
  const { t } = useTranslation();
  const { userEndedSubscription, user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const canWriteEntitySettings = permissions.can(
    Cap.ENTITY_SETTINGS,
    Level.WRITE
  );

  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showSendingDomainUpgradeModal, setShowSendingDomainUpgradeModal] =
    useState(false);

  const { data: emailValidationStatus } = useEmailSenderValidationStatus(
    customer_identification,
    entity_identification
  );

  const { mutate: requestValidationCode, isLoading: isRequestingCode } =
    useRequestEmailSenderValidation();

  const sendingDomainBenefits: PremiumBenefit[] = useMemo(
    () => [
      {
        id: "sending-domain-branding",
        text: t("dashboard.upgrade.sendingDomain.benefit1") as string,
      },
      {
        id: "sending-domain-deliverability",
        text: t("dashboard.upgrade.sendingDomain.benefit2") as string,
      },
    ],
    [t]
  );

  const {
    watch,
    reset,
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
  } = useForm<FormValues>({
    resolver: joiResolver(schema),
    mode: "onBlur",
    defaultValues: { email_address_sender: data?.email_address_sender ?? "" },
  });

  useEffect(() => {
    reset({ email_address_sender: data?.email_address_sender ?? "" });
  }, [data, reset]);

  const { mutate, isLoading } = useUpdateEntityPreferencesByPath();

  const onUpdateSender = (values: FormValues) => {
    mutate(
      {
        user_identification,
        entity_identification,
        path: "email_address_sender",
        value: values.email_address_sender,
      },
      {
        onSuccess: () => {
          toast.success(t("dashboard.entities.entityPreferences.success"));
        },
      }
    );
  };

  const stored = (data?.email_address_sender || "").trim();
  const current = (watch("email_address_sender") || "").trim();
  const storedLc = stored.toLowerCase();
  const currentLc = current.toLowerCase();

  const senderChanged = currentLc !== storedLc && currentLc.length > 0;
  const senderIsReserved = currentLc
    ? isReservedSenderDomain(currentLc)
    : false;

  const handleRequestCode = () => {
    const pendingExpiresAt = emailValidationStatus?.code_expires_at
      ? new Date(emailValidationStatus.code_expires_at).getTime()
      : 0;
    if (pendingExpiresAt > Date.now()) {
      setShowValidationModal(true);
      return;
    }

    requestValidationCode(
      {
        customer_identification,
        entity_identification,
        email: stored || undefined,
      },
      {
        onSuccess: () => setShowValidationModal(true),
      }
    );
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onUpdateSender)} id="emailSenderConfig">
        <fieldset
          disabled={!canWriteEntitySettings}
          className="border-0 m-0 p-0 min-w-0"
        >
          <SenderEmailField
            control={control}
            errors={errors}
            stored={stored}
            current={current}
            emailValidationStatus={emailValidationStatus}
            isRequestingCode={isRequestingCode}
            isSaving={isLoading}
            canSave={
              !userEndedSubscription && isValid && isDirty && !senderIsReserved
            }
            canWriteEntitySettings={canWriteEntitySettings}
            onRequestCode={handleRequestCode}
          />

          {senderChanged && !senderIsReserved && (
            <p className="mb-3 text-sm text-amber-700 font-figtree">
              {t("dashboard.entities.emailSenderValidation.verifyBeforeSave")}
            </p>
          )}

          <EmailDomainCard
            customer_identification={customer_identification}
            entity_identification={entity_identification}
            senderEmail={stored}
            isEnterprise={permissions.isAtLeastTier(PlanTier.ENTERPRISE)}
            canWriteEntitySettings={canWriteEntitySettings}
            onLockedClick={() => setShowSendingDomainUpgradeModal(true)}
          />
        </fieldset>
      </form>

      <UpgradeModal
        showModal={showSendingDomainUpgradeModal}
        setShowModal={setShowSendingDomainUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
        title={t("dashboard.upgrade.sendingDomain.title") as string}
        description={t("dashboard.upgrade.sendingDomain.description") as string}
        benefits={sendingDomainBenefits}
        icon={HiOutlineCube}
        iconTheme="blue"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />
      <EmailSenderValidationModal
        show={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        pendingEmail={watch("email_address_sender")}
      />
    </div>
  );
};

export default EmailSenderSettings;
